import { useEffect } from 'react';

// Client-side per-route SEO. Googlebot renders our JS, so updating <title>, description,
// canonical and OG tags on each route lets Google index every public page distinctly
// (instead of every URL sharing the homepage title).

const BASE = 'https://alerts.pttnz.net';
const SITE = 'PTT NZ Alerts';
const DEFAULT_TITLE = 'PTT NZ Alerts — Live NZ Highway Closures, Traffic & Weather Alerts';
const DEFAULT_DESC =
  'Live New Zealand highway closures, crashes and traffic events plus MetService severe-weather warnings from NZTA Waka Kotahi. Route watch, web push, email & SMS alerts, and freight bridge restrictions.';

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(obj) {
  const id = 'route-jsonld';
  let el = document.getElementById(id);
  if (!obj) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
}

/**
 * @param {{title?: string, description?: string, path?: string, jsonLd?: object|null}} opts
 *  - title: page title WITHOUT the site suffix (omit on the homepage for the full default)
 *  - description: meta description for the page
 *  - path: canonical path (defaults to the current pathname)
 *  - jsonLd: optional structured-data object injected as a managed <script> (e.g. per-alert)
 */
export function useSeo({ title, description, path, jsonLd } = {}) {
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : '';
  useEffect(() => {
    const full = title ? `${title} · ${SITE}` : DEFAULT_TITLE;
    const desc = description || DEFAULT_DESC;
    const url = BASE + (path || window.location.pathname);

    document.title = full;
    upsertMeta('name', 'description', desc);
    upsertMeta('property', 'og:title', full);
    upsertMeta('property', 'og:description', desc);
    upsertMeta('property', 'og:url', url);
    upsertMeta('name', 'twitter:title', full);
    upsertMeta('name', 'twitter:description', desc);
    upsertCanonical(url);
    upsertJsonLd(jsonLd || null);

    return () => upsertJsonLd(null); // clear per-route structured data on unmount
  }, [title, description, path, jsonLdKey]);
}

export default useSeo;
